<template>
  <slot :tweened-value="tweenedValue" :formatted-value="formatNumber(tweenedValue, { maximumFractionDigits: digits })" :digits="digits">
    <span v-bind="$attrs">
      {{ formatNumber(tweenedValue, { maximumFractionDigits: digits }) }}
    </span>
  </slot>
</template>

<script setup lang="ts">
import { watch, ref, onMounted } from "vue";
import { useTransition } from "@vueuse/core";
import { formatNumber } from "@/helpers/utility/Formatting";

const props = withDefaults(defineProps<{
  value: number;
  startValue?: number;
  duration?: number;
  maxDecimalPoints?: number;
}>(), {
  maxDecimalPoints: 3,
  duration: 200,
});

const sourceValue = ref(props.startValue ?? props.value);
watch(() => props.value, (newValue) => {
  sourceValue.value = newValue;
});
onMounted(() => {
  sourceValue.value = props.value;
});

const tweenedValue = useTransition(sourceValue, {
  duration: props.duration,
});

const digits = ref(0);
watch(() => props.value, (newValue, oldValue) => {
  let newDigits = 0, oldDigits = 0;
  if (typeof newValue === "number" && !isNaN(newValue) && isFinite(newValue)) {
    const parts = newValue.toString().split(".");
    if (parts.length > 1) {
      newDigits = parts[1].length;
    }
  }
  if (typeof oldValue === "number" && !isNaN(oldValue) && isFinite(oldValue)) {
    const parts = oldValue.toString().split(".");
    if (parts.length > 1) {
      oldDigits = parts[1].length;
    }
  }
  digits.value = Math.min(Math.max(newDigits, oldDigits, 0), props.maxDecimalPoints);
}, { immediate: true });
watch(() => props.maxDecimalPoints, (newDecimalPoints) => {
  let newDigits = 0;
  if (typeof props.value === "number" && !isNaN(props.value) && isFinite(props.value)) {
    const parts = props.value.toString().split(".");
    if (parts.length > 1) {
      newDigits = parts[1].length;
    }
  }
  digits.value = Math.min(Math.max(newDigits, 0), newDecimalPoints);
});
</script>
